import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import HomeRepairServiceOutlinedIcon from '@mui/icons-material/HomeRepairServiceOutlined';

import Loader from '../../../Components/UI/Loader/Loader';
import ProjectImageCarousel from '../../../Components/Media/ProjectImageCarousel';
import FirebaseImage from '../../../Components/Media/FirebaseImage';
import DemandLocationPanel from '../../../Components/Geo/DemandLocationPanel';
import PostModal from '../../../Components/UI/Modal/PostModal';
import MilestonePaymentPanel from '../../../Components/Payments/MilestonePaymentPanel';
import CompletionConfirmationPanel from '../../../Components/Project/CompletionConfirmationPanel';
import ProjectPulse from '../../../Components/Project/ProjectPulse';
import ProjectConversationPanel from '../../../Components/Project/ProjectConversationPanel';
import DeleteDemand from '../Demand/DeleteDemand';
import ClientCheckEstimation from '../../Jobs/Offers/ClientCheckEstimation';
import ManageEstimationModal from '../ManageEstimationModal';
import ClientOffersModal from '../ClientOffersModal';
import ContractModalClient from '../../Jobs/Offers/ContractModalClient';
import ReviewClientModal from '../../Jobs/Offers/ReviewClientModal';
import ModalDevisPreview from '../../ClientProjects/ModalDevisPreview';
import { getDemandsAPI } from '../../../Store/Actions/demandActions';
import { setDemandPublicPreview } from '../../../Services/demandLifecycle';
import { getProjectDescription, getProjectImages } from '../../../Utils/legacyData';
import { useI18n } from '../../../i18n/I18nProvider';

const STATUS = {
  unassigned: {
    label: 'Recherche en cours', labelEn: 'Searching for an artisan',
    tone: 'neutral',
    description: 'Votre demande est visible par les artisans compatibles.', descriptionEn: 'Your request is visible to matching artisans.',
  },
  estimation_wait: {
    label: 'Proposition reçue', labelEn: 'Proposal received',
    tone: 'attention',
    description: 'Un artisan vous propose une première estimation.', descriptionEn: 'An artisan has sent you an initial estimate.',
  },
  estimation_confirmed: {
    label: 'Visite à confirmer', labelEn: 'Visit to confirm',
    tone: 'attention',
    description: 'Après la visite, confirmez que vous souhaitez poursuivre avec cet artisan.', descriptionEn: 'After the visit, confirm that you want to continue with this artisan.',
  },
  estimation_visit_valid: {
    label: 'Contrat en préparation', labelEn: 'Quote being prepared',
    tone: 'progress',
    description: 'L’artisan prépare le contrat détaillé.', descriptionEn: 'The artisan is preparing the detailed quote and agreement.',
  },
  contract_offered: {
    label: 'Contrat reçu', labelEn: 'Quote received',
    tone: 'attention',
    description: 'Consultez le contrat, puis acceptez-le ou demandez une modification.', descriptionEn: 'Review the quote, then accept it or request changes.',
  },
  modification_asked: {
    label: 'Modification demandée', labelEn: 'Changes requested',
    tone: 'progress',
    description: 'L’artisan examine votre demande de modification.', descriptionEn: 'The artisan is reviewing your requested changes.',
  },
  assigned: {
    label: 'Chantier confirmé', labelEn: 'Project confirmed',
    tone: 'progress',
    description: 'Le contrat est accepté et le chantier peut démarrer.', descriptionEn: 'The quote is accepted and the work can begin.',
  },
  completion_pending: {
    label: 'Fin des travaux à confirmer', labelEn: 'Completion to confirm',
    tone: 'attention',
    description: 'L’artisan a déclaré les travaux terminés. Vérifiez le chantier avant de confirmer ou de signaler un problème.', descriptionEn: 'The artisan marked the work as complete. Check the project before confirming or reporting an issue.',
  },
  completion_disputed: {
    label: 'Problème signalé', labelEn: 'Issue reported',
    tone: 'attention',
    description: 'Le solde reste bloqué pendant vos échanges avec l’artisan.', descriptionEn: 'The remaining payment stays on hold while you discuss the issue with the artisan.',
  },
  finished: {
    label: 'Paiement à effectuer', labelEn: 'Payment due',
    tone: 'attention',
    description: 'Vous avez confirmé la fin des travaux. Vous pouvez maintenant régler le solde.', descriptionEn: 'You confirmed completion. You can now pay the remaining balance.',
  },
  paid: {
    label: 'Avis attendu', labelEn: 'Review requested',
    tone: 'success',
    description: 'Le paiement est confirmé. Partagez votre expérience.', descriptionEn: 'Payment is confirmed. Share your experience.',
  },
};


const demandTypeLabel = (type, pick) => pick(
  ({ window: 'Menuiserie', electricity: 'Électricité' }[type] || 'Travaux'),
  ({ window: 'Joinery', electricity: 'Electrical work' }[type] || 'Work'),
);

const Main = ({ firebase, loading, userDemands = [], getDemands }) => {
  const { pick, formatDate } = useI18n();
  const [postOpen, setPostOpen] = useState(false);
  const [activeModal, setActiveModal] = useState(null);
  const history = useHistory();
  const [selectedDemand, setSelectedDemand] = useState(null);
  const [previewBusyId, setPreviewBusyId] = useState('');

  useEffect(() => {
    const unsubscribe = getDemands();
    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [getDemands]);

  const activeDemands = useMemo(
    () => userDemands.filter((demand) => demand?.demand_status !== 'reviewed'),
    [userDemands],
  );

  // A returning client can have no active demand while still having project history.
  // In that case the empty state must say "new demand", not "first demand".
  const hasDemandHistory = useMemo(
    () => userDemands.some((demand) => Boolean(demand?.demand_id)),
    [userDemands],
  );

  const localizeStatus = (status) => ({
    ...status,
    label: pick(status.label, status.labelEn || status.label),
    description: pick(status.description, status.descriptionEn || status.description),
  });

  const openModal = (name, demand) => {
    setSelectedDemand(demand || null);
    setActiveModal(name);
  };

  const closeModal = () => {
    setActiveModal(null);
  };

  const togglePublicPreview = async (demand) => {
    if (!demand?.demand_id || previewBusyId) return;
    const enabled = demand.public_preview_consent !== true;
    setPreviewBusyId(demand.demand_id);
    try {
      await setDemandPublicPreview({ demandId: demand.demand_id, enabled });
      toast.success(enabled
        ? pick('L’aperçu anonymisé est maintenant visible sur la page publique.', 'The anonymised preview is now visible on the public page.')
        : pick('L’aperçu public de cette demande a été désactivé.', 'The public preview for this request has been disabled.'));
    } catch (error) {
      const message = String(error?.message || pick('Impossible de modifier l’aperçu public.', 'Unable to update the public preview.'));
      toast.error(/^internal$/i.test(message) ? pick('Impossible de modifier l’aperçu public pour le moment.', 'Unable to update the public preview right now.') : message);
    } finally {
      setPreviewBusyId('');
    }
  };

  const renderPrimaryAction = (demand) => {
    const offerCount = Number(demand.offer_count) || 0;
    if (demand.demand_status === 'unassigned' && offerCount > 0) {
      return (
        <PrimaryButton type="button" onClick={() => openModal('offers', demand)}>
          {pick(`Comparer les propositions (${offerCount})`, `Compare proposals (${offerCount})`)}
        </PrimaryButton>
      );
    }

    switch (demand.demand_status) {
      case 'estimation_wait':
        return <PrimaryButton type="button" onClick={() => openModal('estimation', demand)}>{pick('Consulter la proposition', 'View proposal')}</PrimaryButton>;
      case 'estimation_confirmed': {
        const mode = demand?.assessment_workflow?.mode || demand?.estimation?.assessment_mode || 'visit_required';
        if (mode === 'visit_required') {
          return <PrimaryButton type="button" onClick={() => openModal('visit', demand)}>{demand?.visit_confirmation?.artisan_confirmed_at ? pick('Confirmer la visite', 'Confirm visit') : pick('Visite en attente', 'Visit pending')}</PrimaryButton>;
        }
        return null;
      }
      case 'contract_offered':
      case 'modification_asked':
        return <PrimaryButton type="button" onClick={() => openModal('contract', demand)}>{pick('Consulter le contrat', 'View quote')}</PrimaryButton>;
      case 'assigned':
        return <PrimaryButton type="button" onClick={() => openModal('progress', demand)}>{pick('Voir le devis et le suivi', 'View quote and progress')}</PrimaryButton>;
      case 'finished':
        return null;
      case 'paid':
        return <PrimaryButton type="button" onClick={() => openModal('review', demand)}>{pick('Publier un avis', 'Post a review')}</PrimaryButton>;
      default:
        return null;
    }
  };

  const cancellable = (status) => status === 'unassigned';

  return (
    <PageSection>
      <Header>
        <div>
          <Title>{pick('Mes demandes', 'My requests')}</Title>
        </div>
        <CreateButton type="button" onClick={() => setPostOpen(true)} disabled={loading}>
          {pick('Demander un devis', 'Request a quote')}
        </CreateButton>
      </Header>

      {loading && activeDemands.length === 0 ? (
        <LoadingCard><Loader /></LoadingCard>
      ) : activeDemands.length === 0 ? (
        <EmptyState>
          <EmptyIcon><HomeRepairServiceOutlinedIcon /></EmptyIcon>
          <h2>{pick('Aucune demande active', 'No active request')}</h2>
          <p>
            {hasDemandHistory
              ? pick('Vous n’avez aucun projet en cours pour le moment. Lancez une nouvelle demande dès que vous avez besoin d’un artisan.', 'You have no project in progress right now. Create a new request whenever you need an artisan.')
              : pick('Décrivez votre besoin, ajoutez quelques photos et recevez une proposition d’un artisan local.', 'Describe your need, add a few photos and receive a proposal from a local artisan.')}
          </p>
          <PrimaryButton type="button" onClick={() => setPostOpen(true)}>
            {hasDemandHistory ? pick('Créer une nouvelle demande', 'Create a new request') : pick('Créer ma première demande', 'Create my first request')}
          </PrimaryButton>
        </EmptyState>
      ) : (
        <Cards>
          {activeDemands.map((demand) => {
            const offerCount = Number(demand.offer_count) || 0;
            const rawStatus = demand.demand_status === 'unassigned' && offerCount > 0
              ? {
                label: pick(`${offerCount} proposition${offerCount > 1 ? 's' : ''} reçue${offerCount > 1 ? 's' : ''}`, `${offerCount} proposal${offerCount === 1 ? '' : 's'} received`),
                tone: 'attention',
                description: pick('Comparez les disponibilités et choisissez un artisan. La demande reste ouverte tant que vous n’avez rien accepté.', 'Compare availability and choose an artisan. The request stays open until you accept a proposal.'),
              }
              : STATUS[demand.demand_status] || {
                label: pick('Statut à vérifier', 'Status to check'), tone: 'neutral', description: pick('La demande est en cours de traitement.', 'The request is being processed.'),
              };
            const status = localizeStatus(rawStatus);
            const images = getProjectImages(demand);
            const description = getProjectDescription(demand);
            const artisanPhone = demand.contract?.artisan_phone_number || demand.estimation?.artisan_phone_number;
            const showPhone = !['unassigned', 'estimation_wait'].includes(demand.demand_status) && artisanPhone;
            const artisanId = demand.contract?.artisan_id || demand.estimation?.artisan_id || demand.selected_artisan_id;
            const artisanName = [
              demand.contract?.artisan_firstname || demand.estimation?.artisan_firstname,
              demand.contract?.artisan_lastname || demand.estimation?.artisan_lastname,
            ].filter(Boolean).join(' ') || pick('Artisan sélectionné', 'Selected artisan');
            const artisanImage = demand.contract?.artisan_profile_image || demand.estimation?.artisan_profile_image || '';

            return (
              <DemandCard key={demand.demand_id}>
                <ProjectImageCarousel
                  images={images}
                  fallbackSrc="/images/project-placeholder.svg"
                  alt={`Photo du chantier : ${description}`}
                />
                <CardBody>
                  <TopRow>
                    <StatusBadge $tone={status.tone}>{status.label}</StatusBadge>
                    <TopActions>
                      {demand.demand_status === 'unassigned' && (
                        <PreviewToggle
                          type="button"
                          $active={demand.public_preview_consent === true}
                          disabled={previewBusyId === demand.demand_id}
                          onClick={() => togglePublicPreview(demand)}
                        >
                          {previewBusyId === demand.demand_id
                            ? pick('Mise à jour…', 'Updating…')
                            : demand.public_preview_consent === true
                              ? pick('Aperçu public activé', 'Public preview enabled')
                              : pick('Afficher sur la page publique', 'Show on public page')}
                        </PreviewToggle>
                      )}
                      {cancellable(demand.demand_status) && (
                        <IconButton type="button" aria-label={pick('Supprimer cette demande', 'Delete this request')} onClick={() => openModal('delete', demand)}>
                          <DeleteOutlineIcon />
                        </IconButton>
                      )}
                    </TopActions>
                  </TopRow>

                  <CardTitle>{demandTypeLabel(demand.demand_type, pick)}</CardTitle>
                  <Description className="project-description">{description || pick('Aucune description renseignée.', 'No description provided.')}</Description>

                  {artisanId && (
                    <ParticipantCard type="button" onClick={() => history.push('/artisanboutique', { artisanId })}>
                      <ParticipantAvatar src={artisanImage} fallbackSrc="/images/user.svg" alt={`Profil de ${artisanName}`} />
                      <div><small>{pick('Artisan du projet · Voir le profil', 'Project artisan · View profile')}</small><strong>{artisanName}</strong></div>
                      <span>{pick('Profil & avis →', 'Profile & reviews →')}</span>
                    </ParticipantCard>
                  )}

                  <MetaGrid>
                    <MetaItem>
                      <CalendarTodayOutlinedIcon />
                      <span><small>{pick('Publiée le', 'Published')}</small>{formatDate(demand.date)}</span>
                    </MetaItem>
                    <MetaItem>
                      <LocationOnOutlinedIcon />
                      <span><small>{pick('Lieu du chantier', 'Project location')}</small>{demand.demand_address || pick('Adresse non renseignée', 'Address not provided')}</span>
                    </MetaItem>
                    {showPhone && (
                      <MetaItem as="a" href={`tel:${artisanPhone}`}>
                        <PhoneOutlinedIcon />
                        <span><small>{pick('Artisan', 'Artisan')}</small>{artisanPhone}</span>
                      </MetaItem>
                    )}
                  </MetaGrid>

                  <DemandLocationPanel demand={demand} allowNavigation />

                  <StatusExplanation>{status.description}</StatusExplanation>

                  <ProjectPulse demand={demand} role="Client" />

                  <CompletionConfirmationPanel demand={demand} />
                  <MilestonePaymentPanel demand={demand} />
                  {demand.selected_artisan_id || demand.estimation?.artisan_id || demand.contract?.artisan_id ? (
                    <ProjectConversationPanel demand={demand} />
                  ) : null}

                  <CardActions>
                    {renderPrimaryAction(demand)}
                  </CardActions>
                </CardBody>
              </DemandCard>
            );
          })}
        </Cards>
      )}

      <PostModal
        showModal={postOpen ? 'open' : 'close'}
        handleClick={(event) => {
          event?.preventDefault?.();
          setPostOpen((current) => !current);
        }}
      />

      {selectedDemand && (
        <>
          <DeleteDemand show={activeModal === 'delete'} close={closeModal} demand={selectedDemand} />
          <ClientOffersModal opened={activeModal === 'offers'} onClose={closeModal} demand={selectedDemand} />
          <ClientCheckEstimation showModal={activeModal === 'estimation' ? 'open' : 'close'} handleClick={closeModal} demand={selectedDemand} />
          <ManageEstimationModal showModal={activeModal === 'visit' ? 'open' : 'close'} handleClick={closeModal} demand={selectedDemand} />
          <ContractModalClient showModal={activeModal === 'contract' ? 'open' : 'close'} handleClick={closeModal} demand={selectedDemand} />
          <ModalDevisPreview showModal={activeModal === 'progress' ? 'open' : 'close'} handleClick={closeModal} demand={selectedDemand} />
          <ReviewClientModal demand={selectedDemand} show={activeModal === 'review'} close={closeModal} />
        </>
      )}
    </PageSection>
  );
};

const PageSection = styled.section`
  min-width: 0;
`;

const Header = styled.header`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 1rem 1.2rem;

  & > div:first-child {
    min-width: 0;
  }

  margin-bottom: 1.15rem;
  padding: clamp(1.05rem, 2.8vw, 1.55rem);
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 1.15rem;
  background: linear-gradient(135deg, #ffffff 0%, #f8f5ef 100%);
  box-shadow: 0 .85rem 2.3rem rgba(0, 0, 0, 0.065);

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: .85rem;
    padding: 1rem;
    border-radius: 1rem;
  }
`;

const Eyebrow = styled.p`
  margin: 0 0 0.4rem;
  color: var(--color-main);
  font-size: 0.75rem;
  font-weight: 900;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

const Title = styled.h1`
  margin: 0;
  color: var(--color-mainDark);
  font-size: clamp(1.75rem, 4vw, 2.45rem);
`;

const Subtitle = styled.p`
  max-width: 36rem;
  margin: 0.45rem 0 0;
  color: var(--color-textMuted);
  line-height: 1.58;

  @media (max-width: 640px) {
    max-width: none;
    font-size: .93rem;
    line-height: 1.5;
  }
`;

const CreateButton = styled.button`
  min-height: 3rem;
  border: 0;
  border-radius: 999px;
  padding: 0.9rem 1.25rem;
  background: #111;
  color: #fff;
  font: inherit;
  font-weight: 850;
  cursor: pointer;
  white-space: nowrap;
  box-shadow: 0 .8rem 1.8rem rgba(0, 0, 0, 0.16);

  &:hover:not(:disabled) { background: #000; transform: translateY(-1px); }
  &:disabled { opacity: 0.55; cursor: wait; }

  @media (max-width: 640px) {
    width: 100%;
  }
`;

const Cards = styled.div`
  display: grid;
  gap: 1.85rem;
`;

const DemandCard = styled.article`
  min-width: 0;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.09);
  border-radius: 1.35rem;
  background: #fff;
  box-shadow: 0 1rem 2.8rem rgba(0, 0, 0, 0.09);
  transition: transform 180ms ease, box-shadow 180ms ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 1.35rem 3.2rem rgba(0, 0, 0, 0.13);
  }
`;

const CardBody = styled.div`
  min-width: 0;
  padding: clamp(1.45rem, 3.2vw, 2.1rem);
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: .25rem;
`;

const toneStyles = {
  neutral: 'background:#f1ece5;color:#51606e;',
  attention: 'background:#fff4d6;color:#765209;',
  progress: 'background:#e8f1ff;color:#174f93;',
  success: 'background:#e8f7ee;color:#17623a;',
};

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  min-height: 2rem;
  padding: 0.42rem 0.82rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 850;
  ${({ $tone }) => toneStyles[$tone] || toneStyles.neutral}
`;

const TopActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: .55rem;
  flex-wrap: wrap;
`;

const PreviewToggle = styled.button`
  min-height: 2.35rem;
  padding: .48rem .72rem;
  border: 1px solid ${({ $active }) => ($active ? '#b8dbc5' : '#dfd6c8')};
  border-radius: 999px;
  background: ${({ $active }) => ($active ? '#e8f7ee' : '#fbf8f2')};
  color: ${({ $active }) => ($active ? '#17623a' : '#6f5116')};
  font: inherit;
  font-size: .7rem;
  font-weight: 850;
  cursor: pointer;

  &:disabled { opacity: .6; cursor: wait; }
`;

const IconButton = styled.button`
  width: 2.35rem;
  height: 2.35rem;
  display: inline-grid;
  place-items: center;
  border: 0;
  border-radius: 0.7rem;
  background: #fff1f1;
  color: var(--color-errorRed);
  cursor: pointer;

  &:hover { background: #ffe2e2; }
`;

const CardTitle = styled.h2`
  margin: 1.15rem 0 0.5rem;
  color: var(--color-mainDark);
  font-size: clamp(1.35rem, 2.6vw, 1.65rem);
  line-height: 1.2;
`;

const Description = styled.p`
  margin: 0;
  padding-right: .25rem;
  max-width: 52rem;
  color: var(--color-text);
  font-size: 1rem;
  line-height: 1.7;
  white-space: pre-wrap;
`;

const ParticipantCard = styled.button`
  width:100%;display:grid;grid-template-columns:auto minmax(0,1fr) auto;align-items:center;gap:.7rem;margin-top:.8rem;padding:.7rem .8rem;border:1px solid #e6dfd4;border-radius:.9rem;background:#faf8f4;color:#111;font:inherit;text-align:left;cursor:pointer;
  div{display:grid;min-width:0;}small{color:#766f65;font-size:.66rem;}strong{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:.86rem;}span{color:#8a641a;font-size:.68rem;font-weight:850;white-space:nowrap;}
  @media(max-width:32rem){grid-template-columns:auto minmax(0,1fr);span{grid-column:2;}}
`;
const ParticipantAvatar = styled(FirebaseImage)`width:2.7rem;height:2.7rem;border-radius:50%;object-fit:cover;background:#eee9e2;`;
const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: .9rem;
  width: 100%;
  min-width: 0;
  margin-top: 1.45rem;

  /* The content column can be narrow even on a desktop because of sidebars.
     Switch to one column before labels and values begin wrapping into each other. */
  @media (max-width: 78rem) {
    grid-template-columns: 1fr;
  }

  @media (min-width: 78.01rem) {
    & > :only-child {
      grid-column: 1 / -1;
    }
  }
`;

const MetaItem = styled.div`
  min-width: 0;
  width: 100%;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: start;
  gap: 0.75rem;
  padding: .95rem 1rem;
  border-radius: 0.95rem;
  background: #f5f7f9;
  color: var(--color-text);
  text-decoration: none;
  overflow: hidden;

  svg {
    flex: 0 0 auto;
    margin-top: 0.1rem;
    color: var(--color-main);
    font-size: 1.1rem;
  }

  span {
    min-width: 0;
    width: 100%;
    font-size: .94rem;
    font-weight: 750;
    line-height: 1.45;
    overflow-wrap: anywhere;
  }

  small {
    display: block;
    margin-bottom: .24rem;
    color: var(--color-textMuted);
    font-size: .72rem;
    font-weight: 700;
    letter-spacing: .02em;
  }
`;

const StatusExplanation = styled.p`
  margin: 1.15rem 0 0;
  padding: 1rem 1rem;
  border-left: 3px solid var(--color-main);
  border-radius: 0 0.6rem 0.6rem 0;
  background: #faf8f4;
  color: var(--color-textMuted);
  font-size: 0.86rem;
  line-height: 1.55;
`;

const CardActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.85rem;
  margin-top: 1.35rem;
`;

const PrimaryButton = styled.button`
  border: 0;
  border-radius: 0.75rem;
  padding: 0.75rem 1rem;
  background: var(--color-main);
  color: #fff;
  font: inherit;
  font-weight: 800;
  cursor: pointer;

  &:hover { background: var(--color-mainDark); }
`;

const PaymentArea = styled.div`
  width: 100%;
`;

const EmptyState = styled.div`
  display: grid;
  justify-items: center;
  padding: clamp(2rem, 7vw, 4rem) 1.5rem;
  border: 1px dashed rgba(0, 0, 0, 0.2);
  border-radius: 1.35rem;
  background: #fff;
  text-align: center;

  h2 { margin: 0.8rem 0 0.45rem; color: var(--color-mainDark); }
  p { max-width: 560px; margin: 0 0 1.2rem; color: var(--color-textMuted); line-height: 1.6; }
`;

const EmptyIcon = styled.div`
  width: 4rem;
  height: 4rem;
  display: grid;
  place-items: center;
  border-radius: 1.2rem;
  background: #edf4fa;
  color: var(--color-main);
  svg { font-size: 2rem; }
`;

const LoadingCard = styled.div`
  min-height: 260px;
  display: grid;
  place-items: center;
  border-radius: 1.35rem;
  background: #fff;
`;

const mapStateToProps = ({ firebase, demands }) => ({
  firebase,
  loading: demands.loading,
  userDemands: demands.demands,
});

const mapDispatchToProps = (dispatch) => ({
  getDemands: () => dispatch(getDemandsAPI()),
});

export default connect(mapStateToProps, mapDispatchToProps)(Main);
